import { useState } from "react"
import { ArrowsLeftRight } from "@phosphor-icons/react"
import { useInspectorContext } from "~/components/design-system/pages/internal/InspectorContext"
import { cn } from "~/utils/helpers"

/**
 * Drag handle + divider in the gutter between the main content and the inspector. Rendered
 * as a flex sibling in `Module`. While dragging it shows a ghost line that follows the cursor
 * and only commits the new width on mouse-up — so the inspector and the table beside it reflow
 * once, not on every frame (which otherwise makes the AG-Grid table flicker).
 */
const InspectorResizeHandle = () => {
  const { inspector, isOpen, width, minWidth, maxWidth, commitWidth } = useInspectorContext()
  // Ghost-line offset in px from the resting seam (negative = dragged left = inspector grows).
  // Null when idle.
  const [ghostOffset, setGhostOffset] = useState<number | null>(null)
  const isDragging = ghostOffset !== null

  if (!inspector || !isOpen) return null

  // Right-docked inspector: dragging left widens it. Clamp so the committed width stays in range.
  // Measured from where the inspector actually IS, expansion included — the seam has to track the
  // cursor, so grabbing it while expanded must not snap the target back to the pre-expansion
  // width. Committing from here ends the expansion (see `commitWidth`), which is right: the user
  // grabbed the edge of the wide inspector and dragged it deliberately.
  const widthFor = (clientX: number, startX: number) =>
    Math.max(minWidth, Math.min(maxWidth, width + (startX - clientX)))

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    const startX = e.clientX
    document.body.style.cursor = "col-resize"
    document.body.style.userSelect = "none"

    const handleMouseMove = (event: MouseEvent) =>
      setGhostOffset(width - widthFor(event.clientX, startX))
    const handleMouseUp = (event: MouseEvent) => {
      commitWidth(widthFor(event.clientX, startX))
      setGhostOffset(null)
      document.body.style.cursor = ""
      document.body.style.userSelect = ""
      window.removeEventListener("mousemove", handleMouseMove)
      window.removeEventListener("mouseup", handleMouseUp)
    }
    window.addEventListener("mousemove", handleMouseMove)
    window.addEventListener("mouseup", handleMouseUp)
  }

  return (
    <div
      onMouseDown={handleMouseDown}
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize inspector"
      className="group relative z-10 flex h-full w-2 shrink-0 cursor-col-resize items-stretch justify-center"
    >
      {/* Resting divider on the seam — thickens on hover. */}
      <div
        className={cn(
          "w-px bg-border-base transition-all group-hover:w-0.5 group-hover:bg-border-strong",
          isDragging && "opacity-0",
        )}
      />

      {/* Thick accent ghost line previewing where the inspector edge will land. */}
      {isDragging && (
        <div
          className="pointer-events-none absolute top-0 left-1/2 h-full w-0.5 bg-button-primary"
          style={{ transform: `translateX(calc(-50% + ${ghostOffset}px))` }}
        />
      )}

      {/* Grabber with bidirectional arrows: fades in on hover, turns accent + travels with the
          ghost line while dragging — a clear "you can / are moving this" affordance. */}
      <div
        className={cn(
          "pointer-events-none absolute top-1/2 left-1/2 flex h-8 w-4 items-center justify-center rounded-full transition-[opacity,background-color]",
          isDragging
            ? "bg-button-primary text-fg-on-color opacity-100"
            : "border border-base bg-component text-fg-subtle opacity-0 shadow-card-rest group-hover:opacity-100",
        )}
        style={{ transform: `translate(calc(-50% + ${ghostOffset ?? 0}px), -50%)` }}
      >
        <ArrowsLeftRight size={12} weight="bold" />
      </div>
    </div>
  )
}

export default InspectorResizeHandle
